/* Created by Interactive 63 - https://interactive63.com */
/**
 * 2D affine matrices in the SVG order [a, b, c, d, e, f], with parsing and formatting of the
 * `transform` attribute. Used to keep artwork where it is when a layer changes parent.
 */

export type Matrix = readonly [number, number, number, number, number, number];

export const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

/** Decimal places kept when a matrix is written back to an attribute. */
const FORMAT_DECIMALS = 4;
/** Differences below this are treated as zero when comparing matrix terms. */
const EPSILON = 1e-9;

/** Multiplies two matrices: the result applies `right` first, then `left`. */
export function multiply(left: Matrix, right: Matrix): Matrix {
  const [a1, b1, c1, d1, e1, f1] = left;
  const [a2, b2, c2, d2, e2, f2] = right;
  return [
    a1 * a2 + c1 * b2,
    b1 * a2 + d1 * b2,
    a1 * c2 + c1 * d2,
    b1 * c2 + d1 * d2,
    a1 * e2 + c1 * f2 + e1,
    b1 * e2 + d1 * f2 + f1,
  ];
}

/** The inverse of a matrix, or null when it flattens the plane and cannot be reversed. */
export function invert(m: Matrix): Matrix | null {
  const [a, b, c, d, e, f] = m;
  const det = a * d - b * c;
  if (Math.abs(det) < EPSILON) return null;
  return [d / det, -b / det, -c / det, a / det, (c * f - d * e) / det, (b * e - a * f) / det];
}

/** Whether a matrix leaves every point where it is. */
export function isIdentity(m: Matrix): boolean {
  return m.every((value, i) => Math.abs(value - (IDENTITY[i] ?? 0)) < EPSILON);
}

/** Splits the argument text of one transform function into numbers, or null if any is invalid. */
function numbers(text: string): number[] | null {
  const parts = text.trim() === '' ? [] : text.trim().split(/[\s,]+/);
  const values = parts.map(Number);
  return values.every(Number.isFinite) ? values : null;
}

/** The matrix for one named transform function, or null when its arguments do not fit. */
function functionMatrix(name: string, args: readonly number[]): Matrix | null {
  const [x = 0, y, z] = args;
  switch (name) {
    case 'matrix':
      return args.length === 6 ? (args as unknown as Matrix) : null;
    case 'translate':
      return args.length === 1 || args.length === 2 ? [1, 0, 0, 1, x, y ?? 0] : null;
    case 'scale':
      return args.length === 1 || args.length === 2 ? [x, 0, 0, y ?? x, 0, 0] : null;
    case 'rotate': {
      if (args.length !== 1 && args.length !== 3) return null;
      const angle = (x * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const turn: Matrix = [cos, sin, -sin, cos, 0, 0];
      if (args.length === 1) return turn;
      const cx = y ?? 0;
      const cy = z ?? 0;
      return multiply([1, 0, 0, 1, cx, cy], multiply(turn, [1, 0, 0, 1, -cx, -cy]));
    }
    case 'skewX':
      return args.length === 1 ? [1, 0, Math.tan((x * Math.PI) / 180), 1, 0, 0] : null;
    case 'skewY':
      return args.length === 1 ? [1, Math.tan((x * Math.PI) / 180), 0, 1, 0, 0] : null;
    default:
      return null;
  }
}

/**
 * Parses a `transform` attribute into one matrix. An empty or missing attribute is the identity;
 * an attribute that cannot be read returns null so callers can leave the element alone.
 */
export function parseTransform(attribute: string | null): Matrix | null {
  if (attribute === null || attribute.trim() === '') return IDENTITY;
  const pattern = /([A-Za-z]+)\s*\(([^)]*)\)/g;
  let result: Matrix = IDENTITY;
  let matched = false;
  for (const match of attribute.matchAll(pattern)) {
    const args = numbers(match[2] ?? '');
    const next = args ? functionMatrix(match[1] ?? '', args) : null;
    if (!next) return null;
    result = multiply(result, next);
    matched = true;
  }
  const leftover = attribute.replace(pattern, '').replace(/[\s,]+/g, '');
  return matched && leftover === '' ? result : null;
}

/** Rounds to the stored precision and removes a negative zero. */
function round(value: number): number {
  const factor = 10 ** FORMAT_DECIMALS;
  return Math.round(value * factor) / factor + 0;
}

/**
 * Writes a matrix as a `transform` attribute value: empty for the identity, `translate` for a
 * pure move, otherwise `matrix`.
 */
export function formatTransform(m: Matrix): string {
  if (isIdentity(m)) return '';
  const [a, b, c, d, e, f] = m.map(round);
  if (a === 1 && b === 0 && c === 0 && d === 1) return `translate(${e} ${f})`;
  return `matrix(${a} ${b} ${c} ${d} ${e} ${f})`;
}
